import { createElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDevEnv } from 'service/envConfig';
import { getToken } from '@dvag/dfs-orchestrator-client';
import { getEnvironmentAppUrls } from '@dvag/dfs-constant-config';
import { createRegisteredChildMfe } from 'mfeBridge/createRegisteredChildMfe';

type AdressaenderungEventOptions = {
  kundennummer?: string;
};

type Props = {
  onBack?: (data?: unknown) => void;
  onClose?: (data?: unknown) => void;
};

type BridgeHandlers = Pick<Props, 'onBack' | 'onClose'>;

const mfeBridgeIdentifier = 'dvag-kundenstammdaten-adressaenderung';
const adressaenderungBridge = createRegisteredChildMfe<BridgeHandlers, AdressaenderungEventOptions>(
  mfeBridgeIdentifier,
);

export const KssAddressEdit = ({ onBack, onClose }: Props) => {
  const { personId } = useParams<{ personId: string }>();

  useEffect(
    () =>
      adressaenderungBridge.bindHandlers({
        onBack,
        onClose,
      }),
    [onBack, onClose],
  );

  useEffect(() => {
    adressaenderungBridge.register((eventName, options, handlers) => {
      if (!handlers) {
        return;
      }

      if (eventName === 'back') {
        handlers.onBack?.(options);
        return;
      }

      if (eventName === 'close') {
        handlers.onClose?.(options);
      }
    });
  }, []);

  useEffect(() => {
    const env = getDevEnv();
    const appUrls = getEnvironmentAppUrls(env);
    const scriptSrc = appUrls.kundenstammdaten + '/adressaenderung/main.js';

    type AdressaenderungElement = HTMLElement & {
      authenticationToken?: () => Promise<string | void>;
      kundennummer?: string;
    };

    const getElement = (): AdressaenderungElement | null =>
      document.querySelector('dvag-kundenstammdaten-adressaenderung');

    const setAuthenticationProvider = (): void => {
      const element = getElement();

      if (!element) {
        return;
      }

      element.authenticationToken = (): Promise<string | void> => Promise.resolve(getToken());

      if (personId) {
        element.kundennummer = personId;
      }
    };

    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.type = 'module';
      script.addEventListener('load', setAuthenticationProvider);
      document.head.appendChild(script);
    }

    setAuthenticationProvider();
  }, [personId]);

  return createElement('dvag-kundenstammdaten-adressaenderung', {
    'mfe-bridge-identifier': mfeBridgeIdentifier,
  });
};
